import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export type BookingActionCalloutTone = "primary" | "warning";

const TONE_CLASS: Record<
  BookingActionCalloutTone,
  { shell: string; icon: string }
> = {
  primary: {
    shell: "border-primary-200 bg-primary-50/50 dark:bg-primary-50/40",
    icon: "bg-primary-100 text-primary-700 ring-primary-600/15",
  },
  warning: {
    shell: "border-warning-200 bg-warning-50/60 dark:bg-warning-50/40",
    icon: "bg-warning-100 text-warning-700 ring-warning-600/20",
  },
};

interface BookingActionCalloutProps {
  tone: BookingActionCalloutTone;
  icon: LucideIcon;
  title: string;
  description: string;
  /** CTA (button / upload control), right-aligned on desktop. */
  action: ReactNode;
  className?: string;
}

/** Next-step banner on the partner booking detail (receipt, completion). */
export function BookingActionCallout({
  tone,
  icon: Icon,
  title,
  description,
  action,
  className,
}: BookingActionCalloutProps) {
  const toneClass = TONE_CLASS[tone];
  return (
    <div
      className={cn(
        "flex shrink-0 flex-col gap-3 rounded-xl border px-4 py-3.5 shadow-xs sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-5",
        toneClass.shell,
        className,
      )}
    >
      <div className="flex min-w-0 items-start gap-3">
        <span
          className={cn(
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ring-1 ring-inset",
            toneClass.icon,
          )}
        >
          <Icon className="h-4 w-4" aria-hidden />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-surface-900">{title}</p>
          <p className="mt-0.5 text-[13px] text-surface-600">{description}</p>
        </div>
      </div>
      <div className="w-full shrink-0 sm:w-auto">{action}</div>
    </div>
  );
}
